import React from 'react';
import Image from 'next/image'
import Box from "@mui/material/Box";
import {Maybe} from "../../../packages/graphql-layer/src";
import {buildCdnUrl, hasFallbackUrl, isCdnEnabled, isCdnUrl} from "../../utility/cdnPictureUtil";
import {usePreviewContext} from "../../context/PreviewContext";
import {CmPictureType} from "../types";

export interface Props {
    picture?: CmPictureType | null | undefined,
    uri?: string,
    name?: string,
    alt?: string,
    orientation?: string,
    width: string,
    height: string,
    maxSize?: boolean,
    fullHeight?: boolean,
    maxWidth?: string,
    maxHeight?: string,
    size?: string,
    originalSize?: boolean,
    sxImage?: any,
    sxContainer?: any,
    isRichtextElement?: boolean,
    title?: string,
    hasPriority?: boolean,
    imageOverlay?: React.ReactNode
}

const defaultContainerStyle = {
    position: "relative",
    maxWidth: "100%",
    maxHeight: "100%",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    height: "auto",
}

const defaultImageStyle = {
    maxWidth: "100%",
    maxHeight: "100%",
    width: "100%",
    height: "auto",
}

const originalSizeImageStyle = {
    maxWidth: "100%",
    maxHeight: "100%",
    width: "auto",
    height: "auto",
}

const maxSizeImageStyle = {
    minWidth: "100%",
    minHeight: "100%",
    width: "100%",
    height: "auto"
}

const fullHeightImageStyle = {
    height: undefined,
    minHeight: "100%",
    maxHeight: "100%",
    width: '100%',
}

const maxWidthImageStyle = {
    maxWidth: "150px",
    maxHeight: "200px",
    width: "auto",
    height: "auto",
}

const smImageStyle = {
    maxWidth: "50px",
    maxHeight: "50px",
    width: "auto",
    height: "auto",
}

const xsImageStyle = {
    maxWidth: "35px",
    maxHeight: "35px",
    width: "auto",
    height: "auto"
}

enum Size {
    xs = 'xs',
    sm = 'sm'
}

export const PICTURE_FALLBACK_WIDTH = '320';

export const PICTURE_DEFAULT_WIDTH = new Map<string, string>([
    ['portrait_ratio1x1',
        PICTURE_FALLBACK_WIDTH],
    ['portrait_ratio3x4', '324'],
    ['landscape_ratio7x2', '585'],
    ['landscape_ratio4x3',
        PICTURE_FALLBACK_WIDTH],
    ['landscape_ratio20x7', '585'],
    ['landscape_ratio16x9',
        PICTURE_FALLBACK_WIDTH]
]);

export const FALLBACK_ORIENTATION = 'portrait_ratio1x1';

export const enum EXTENSION {
    JPG = '.jpg',
    WEBP = '.webp',
    SVG = '.svg'
}

export function calculateWidth(width: string, orientation?: string): string {
    if (width.indexOf('%') > -1 || width.indexOf('px') > -1) {
        if (!orientation) {
            orientation = FALLBACK_ORIENTATION;
        }
        return PICTURE_DEFAULT_WIDTH.get(orientation) ?? PICTURE_FALLBACK_WIDTH;
    } else {
        return width;
    }
}

export function getAltText(alt?: string | null, title?: string | null): string {
    if (alt === '-') {
        return '';
    }
    return alt || title || '';
}

const replacePlaceholder = (imgUrl: string, orientation: string | undefined, imgWidth: string, rewriteExtensionToWebp: boolean = true) => {
    imgUrl = imgUrl.replace('{cropName}', orientation ?? FALLBACK_ORIENTATION);
    imgUrl = imgUrl.replace('{width}', imgWidth);
    return rewriteExtensionToWebp ? imgUrl.replace(EXTENSION.JPG, EXTENSION.WEBP) : imgUrl;
};

/**
 * Get the picture uri. It first tries to get the uri out of uriTemplate, if that is not available it tries to get the uri out of data.uri.
 * If it can't find any uri it returns undefined.
 * If uriTemplate is available it replaces jpg with webp.
 * @param width the width to use
 * @param picture the picture object
 * @param isPreview
 * @param orientation one of the predefined ratios.
 */
export function getPictureUri(width: string, isPreview: boolean, picture?: Maybe<CmPictureType> | any, orientation?: string): string | undefined {
    const imgWidth = calculateWidth(width, orientation);
    let cdnUrl: string | undefined = undefined;
    if (isCdnEnabled(picture)) {
        cdnUrl = buildCdnUrl(picture, orientation ?? FALLBACK_ORIENTATION, imgWidth, isPreview);
    }
    if (cdnUrl) {
        return cdnUrl;
    }
    if (hasFallbackUrl(picture) && picture?.urlPattern) {
        return replacePlaceholder(picture?.urlPattern, orientation, imgWidth, false);
    }
    if (picture && picture?.uriTemplate) {
        let imgUrl = picture?.uriTemplate;
        imgUrl = replacePlaceholder(imgUrl, orientation, imgWidth);
        imgUrl = imgUrl.replace('{cropName}', orientation ?? FALLBACK_ORIENTATION);
        imgUrl = imgUrl.replace('{width}', imgWidth);
        imgUrl = imgUrl.replace(EXTENSION.JPG, EXTENSION.WEBP);
        return '/media' + imgUrl;
    }
    return createPictureUri(imgWidth, picture?.data?.uri, orientation);
}

export function createPictureUri(width: string, uri?: string | null | undefined, orientation?: string): string | undefined {
    if (!uri) {
        return undefined;
    }

    // A Celum CDN URL (see SosContentLinkResolver#resolveCmPictureLink) is already a complete,
    // crop-specific URL - unlike a plain CoreMedia blob URI, it must not get "/orientation/width"
    // appended.
    if (uri.indexOf('soskinderdorfprodexport') > 0) {
        return uri;
    }
    orientation = orientation ? orientation : FALLBACK_ORIENTATION;
    return uri + '/' + orientation + '/' + width;
}

const CCOPicture = (props: Props) => {
    const preview = usePreviewContext().isPreview;

    function renderImg(props: Props) {
        let imgUrl = getPictureUri(props.width, preview, props.picture, props.orientation);
        if (!imgUrl) {
            imgUrl = createPictureUri(props.width, props.uri, props.orientation);
        }
        return (<Image {...(props.hasPriority ? {priority: true} : {loading: 'lazy' as const})}
                       // Celum-Renditionen am Bild-Optimizer vorbei: siehe isCdnUrl.
                       unoptimized={isCdnUrl(imgUrl)}
                       alt={getAltText(props.alt, props.title)}
                       src={imgUrl ?? '#'}
                       width={props.maxWidth
                           ? +props.maxWidth
                           : +props.width}
                       height={props.maxHeight
                           ? +props.maxHeight
                           : +props.height}
                       about={''}
                       style={props.fullHeight ?
                           fullHeightImageStyle
                           : props.maxSize
                               ? maxSizeImageStyle : props.maxWidth
                                   ? maxWidthImageStyle
                                   : props.size === Size.sm
                                       ? smImageStyle
                                       : props.size === Size.xs
                                           ? xsImageStyle
                                           : styleImage}
        />)
    }

    const uri = props.picture ? props.picture?.data?.uri : props.uri;

    const styleContainer = props.fullHeight ?
        {
            ...defaultContainerStyle,
            ...props.sxContainer,
            width: 'auto',
            minHeight: '100%',
        }
        : props.maxSize ? {
                ...defaultContainerStyle,
                ...props.sxContainer,
                maxWidth: `${props.width}px`,
                maxHeight: `${props.height}px`,
            }
            : {
                ...defaultContainerStyle,
                ...props.sxContainer,
            };

    const styleImage = props.sxImage ? {
        ...defaultImageStyle,
        ...props.sxImage
    } : props.originalSize ?
        {
            ...originalSizeImageStyle
        } :
        {
            ...defaultImageStyle
        };

    if (props.name && uri && !uri.startsWith('Content[')) {
        return !props.isRichtextElement ? <Box component="div"
                                               sx={styleContainer}>
            {renderImg(props)}
            {props.imageOverlay}
        </Box> : renderImg(props)
    } else {
        return null
    }
};

export default CCOPicture;
